import { useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY, GOLD, WHITE } from '../lib/ui';
import { Card, Empty, Btn, Select, Field } from '../lib/ui';

const BUCKET = 'documents';

const fmt = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const fmtSize = b => b < 1024 ? `${b} B` : b < 1024*1024 ? `${(b/1024).toFixed(1)} KB` : `${(b/1024/1024).toFixed(1)} MB`;

const fileIcon = (name) => {
  const ext = name.split('.').pop().toLowerCase();
  if (['pdf'].includes(ext))                          return { icon: '📄', color: '#C0392B' };
  if (['doc','docx'].includes(ext))                   return { icon: '📝', color: '#2980B9' };
  if (['xls','xlsx','csv'].includes(ext))             return { icon: '📊', color: '#27AE60' };
  if (['jpg','jpeg','png','gif','webp'].includes(ext)) return { icon: '🖼', color: '#8E44AD' };
  if (['zip','rar','7z'].includes(ext))               return { icon: '🗜', color: '#E67E22' };
  return { icon: '📎', color: '#7F8C8D' };
};

export default function DocumentSection({ linkedType, linkedId, team, docs, onDocsChange }) {
  const [uploading, setUploading]   = useState(false);
  const [dragOver,  setDragOver]    = useState(false);
  const [error,     setError]       = useState(null);
  const [uploader,  setUploader]    = useState(team[0]?.name || '');
  const fileRef = useRef();

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        // Build a unique storage path
        const safeName  = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const timestamp = Date.now();
        const path      = `${linkedType}/${linkedId}/${timestamp}_${safeName}`;

        // Upload to Supabase Storage
        const { error: upErr } = await supabase.storage
          .from(BUCKET)
          .upload(path, file, { upsert: false });
        if (upErr) throw upErr;

        // Save metadata to documents table
        const { data: doc, error: dbErr } = await supabase
          .from('documents')
          .insert([{
            linked_type : linkedType,
            linked_id   : linkedId,
            file_name   : file.name,
            file_size   : file.size,
            storage_path: path,
            uploaded_by : uploader,
          }])
          .select()
          .single();
        if (dbErr) throw dbErr;

        onDocsChange(prev => [doc, ...prev]);
      }
    } catch (e) {
      setError(e.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(doc.storage_path, 300);
    if (error) { alert('Could not get download link: ' + error.message); return; }
    // Open in same tab — most reliable on iOS Safari and Android
    window.location.href = data.signedUrl;
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.file_name}"? This cannot be undone.`)) return;
    const { error: stErr } = await supabase.storage
      .from(BUCKET)
      .remove([doc.storage_path]);
    if (stErr) { alert('Storage error: ' + stErr.message); return; }
    const { error: dbErr } = await supabase
      .from('documents')
      .delete()
      .eq('id', doc.id);
    if (dbErr) { alert('Database error: ' + dbErr.message); return; }
    onDocsChange(prev => prev.filter(d => d.id !== doc.id));
  };

  return (
    <Card title={`Documents (${docs.length})`}>

      {/* Uploader selector + drop zone */}
      <div style={{ marginBottom: 14 }}>
        <Field label="Uploading as">
          <Select value={uploader} onChange={e => setUploader(e.target.value)}
            style={{ maxWidth: 220, fontSize: 12, padding: '5px 8px' }}>
            {team.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
          </Select>
        </Field>
      </div>

      {/* Drop zone */}
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={e  => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
        style={{
          border       : `2px dashed ${dragOver ? GOLD : '#D0CBBF'}`,
          borderRadius : 8,
          padding      : '20px 16px',
          textAlign    : 'center',
          cursor       : 'pointer',
          background   : dragOver ? GOLD + '0A' : '#FAFAF8',
          transition   : 'all 0.15s',
          marginBottom : 16,
        }}>
        <input
          ref={fileRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={e => uploadFiles(e.target.files)}
        />
        {uploading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
            <div style={{ width: 18, height: 18, border: `2px solid ${GOLD}44`, borderTop: `2px solid ${GOLD}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: 13, color: '#888' }}>Uploading…</span>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 28, marginBottom: 6 }}>📎</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: NAVY }}>Click to upload or drag & drop files here</div>
            <div style={{ fontSize: 11, color: '#AAA', marginTop: 4 }}>PDF, Word, Excel, images — any file type</div>
          </>
        )}
      </div>

      {error && (
        <div style={{ background: '#FDF0EE', border: '1px solid #F5C0B8', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#C0392B', marginBottom: 12 }}>
          ⚠ {error}
        </div>
      )}

      {/* File list */}
      {docs.length === 0
        ? <Empty text="No documents uploaded yet." />
        : docs.map(doc => {
            const { icon, color } = fileIcon(doc.file_name);
            return (
              <div key={doc.id} style={{ borderBottom: '1px solid #F0EDE8' }}>
                {/* Tap entire row to download on mobile */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0' }}>
                  <div style={{ fontSize: 24, flexShrink: 0 }}>{icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: NAVY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {doc.file_name}
                    </div>
                    <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                      {fmtSize(doc.file_size)} · {doc.uploaded_by} · {fmt(doc.created_at?.slice(0,10))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      onClick={() => handleDownload(doc)}
                      style={{
                        background: NAVY, color: GOLD, border: 'none', borderRadius: 8,
                        padding: '10px 16px', cursor: 'pointer', fontSize: 13,
                        fontWeight: 700, fontFamily: 'inherit',
                        minWidth: 44, minHeight: 44, // minimum tap target for mobile
                      }}>
                      ↓
                    </button>
                    <button
                      onClick={() => handleDelete(doc)}
                      style={{
                        background: 'none', border: '1px solid #E0D8D0', borderRadius: 8,
                        padding: '10px 12px', cursor: 'pointer', fontSize: 16, color: '#C0392B',
                        minWidth: 44, minHeight: 44,
                      }}>
                      ×
                    </button>
                  </div>
                </div>
              </div>
            );
          })
      }
    </Card>
  );
}
